import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import { ChevronDown, ChevronRight } from 'react-feather';

import { VpIcon, VpCheckbox, VpLink } from '../../';
import VpMediaSubTable from './VpMediaSubTable';
import VpMediaIconList from './VpMediaIconList';
import VpSubmissionInfoMenu from './VpSubmissionInfoMenu';
import { useMediaTableContext } from '../../../hooks';
import { StyledIcon, StyledTableRow, StyledChevronIcon, StyledTableCell } from './styles';
import { formatDateMmDdYyyy } from '../../../utils';

export const VpMediaTableBodyRow = ({ submission = {} }) => {
  const {
    onSubmissionCheck,
    getIsSubmissionPartialSelected,
    getIsSubmissionAllSelected,
    setSelectedSubmissionId,
    setSelectedMediaId
  } = useMediaTableContext();
  const submissionId = submission?.campaignSubmissionId;
  const [isOpen, setIsOpen] = useState(false);

  const formattedDate = useMemo(
    () => formatDateMmDdYyyy(submission?.submissionDate),
    [submission?.submissionDate]
  );
  const hasMedia = !!(submission?.videoContent?.length || submission?.attachmentsContent?.length);

  const onRowClick = () => {
    setSelectedSubmissionId(submissionId);
    //Sets media to null if clicking parent row
    setSelectedMediaId(null);
  };
  const onParentChevClick = (e) => {
    e.stopPropagation();
    setIsOpen((prevState) => !prevState);
  };
  const onMediaRowClick = (mediaId) => {
    setSelectedSubmissionId(submissionId);
    setSelectedMediaId(mediaId);
  };
  const onCheckboxClick = (e) => {
    e.stopPropagation();
    onSubmissionCheck(submissionId);
  };

  useEffect(() => {
    setIsOpen(false);
    //Not sure if necessary: prevents selectedMedia displaying on other submissions
    //Doesn't seem like it changes anything but makes sense to set it to null on close
    setSelectedMediaId(null);
  }, [submissionId]);

  if (!submission?.enabled) {
    const mediaCount = submission?.videoContent?.length + submission?.attachmentsContent?.length;
    const chipCountLabel = mediaCount ? `${mediaCount} media collected` : 'No media collected';
    const expireMessage = 'Submission Locked!';

    return (
      <StyledTableRow key={submissionId} isExpired padding="checkbox">
        <TableCell></TableCell>
        <TableCell padding="checkbox">
          <StyledIcon icon="Lock" />
        </TableCell>
        <TableCell colSpan={3}>
          <strong>{expireMessage}</strong> If you’d like access to this, upgrade now on your{' '}
          <VpLink underline="hover" to="/billing">
            billing page
          </VpLink>
          .
        </TableCell>
        <TableCell>
          <Chip variant="colorTag" label={chipCountLabel} />
        </TableCell>
        <TableCell>{formattedDate}</TableCell>
        <TableCell></TableCell>
      </StyledTableRow>
    );
  }

  return (
    <>
      <StyledTableRow hover key={submissionId} onClick={onRowClick}>
        <TableCell padding="checkbox">
          {hasMedia && (
            <StyledChevronIcon fontSize="large" onClick={onParentChevClick}>
              <VpIcon>{isOpen ? <ChevronDown /> : <ChevronRight />}</VpIcon>
            </StyledChevronIcon>
          )}
        </TableCell>
        <TableCell padding="checkbox">
          {hasMedia && (
            <VpCheckbox
              indeterminate={getIsSubmissionPartialSelected(submissionId)}
              checked={getIsSubmissionAllSelected(submissionId)}
              onClick={onCheckboxClick}
            />
          )}
        </TableCell>
        <TableCell>{submission?.campaignName}</TableCell>
        <TableCell>{submission?.submitterName}</TableCell>
        <TableCell>{submission?.submitterEmail}</TableCell>
        <TableCell>
          {hasMedia ? (
            <VpMediaIconList
              attachments={submission?.attachmentsContent}
              videos={submission?.videoContent}
            />
          ) : (
            <Chip label="No media collected" />
          )}
        </TableCell>
        <TableCell>{formattedDate}</TableCell>
        <TableCell>
          <VpSubmissionInfoMenu submission={submission} canDownload={hasMedia} />
        </TableCell>
      </StyledTableRow>
      {hasMedia && (
        <TableRow>
          <StyledTableCell colSpan={8}>
            <Collapse unmountOnExit in={isOpen}>
              <VpMediaSubTable
                onMediaRowClick={onMediaRowClick}
                videos={submission?.videoContent}
                attachments={submission?.attachmentsContent}
              />
            </Collapse>
          </StyledTableCell>
        </TableRow>
      )}
    </>
  );
};

VpMediaTableBodyRow.propTypes = {
  submission: PropTypes.object,
  submissionExpireDays: PropTypes.number
};

export default VpMediaTableBodyRow;



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpMediaTable/VpMediaTableBodyRow.jsx